import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useExecutionHistory } from '@/core/hooks'
import { useConnections } from '@/core/connection/connection.context'
import {
  EmptyState,
  ErrorNotice,
  Loading,
  formatDuration,
  relativeTime,
} from '@/shared/components'
import { Constellation } from '@/shared/components/Constellation'

const PAGE_SIZE = 25

export function ExecutionsPage() {
  const [params, setParams] = useSearchParams()
  const { connections } = useConnections()
  const [offset, setOffset] = useState(0)

  const connectionId = params.get('connection') ?? undefined
  const status = (params.get('status') as 'success' | 'failed' | null) ?? undefined

  const history = useExecutionHistory({
    connection_id: connectionId,
    status,
    limit: PAGE_SIZE,
    offset,
  })

  const setFilter = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value) next.set(key, value)
    else next.delete(key)
    setParams(next, { replace: true })
    setOffset(0)
  }

  return (
    <div className="page">
      <div className="page-header hero">
        <Constellation />
        <div className="hero-text">
          <h1>History</h1>
          <p>
            Every run, with inputs and outputs redacted and size-capped before they
            were stored.
          </p>
        </div>
      </div>

      <div className="row" style={{ marginBottom: 14 }}>
        <select
          className="select"
          style={{ maxWidth: 280 }}
          value={connectionId ?? ''}
          onChange={(e) => setFilter('connection', e.target.value || null)}
        >
          <option value="">All connections</option>
          {connections.map((c) => (
            <option key={c.connection_id} value={c.connection_id}>
              {c.label}
            </option>
          ))}
        </select>
        <select
          className="select"
          style={{ maxWidth: 160 }}
          value={status ?? ''}
          onChange={(e) => setFilter('status', e.target.value || null)}
        >
          <option value="">Any status</option>
          <option value="success">Succeeded</option>
          <option value="failed">Failed</option>
        </select>
        <button className="btn" onClick={() => history.refetch()}>
          Refresh
        </button>
        <div className="spacer" />
        {history.data && (
          <span className="small faint">
            {history.data.total} total
          </span>
        )}
      </div>

      {history.isLoading && <Loading />}
      {history.error && <ErrorNotice error={history.error} onRetry={() => history.refetch()} />}

      {history.data?.executions.length === 0 && (
        <EmptyState
          title="No runs yet"
          action={
            <Link className="btn btn-primary" to="/run">
              Run an action
            </Link>
          }
        >
          Every action you run — and every ad-hoc request — shows up here.
        </EmptyState>
      )}

      {history.data && history.data.executions.length > 0 && (
        <>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 74 }}>Status</th>
                  <th>Connector</th>
                  <th>Action</th>
                  <th style={{ width: 84 }}>Duration</th>
                  <th style={{ width: 62 }}>Pages</th>
                  <th style={{ width: 104 }}>When</th>
                </tr>
              </thead>
              <tbody>
                {history.data.executions.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span
                        className={`badge ${row.status === 'success' ? 'badge-ok' : 'badge-danger'}`}
                      >
                        {row.status_code ?? (row.status === 'success' ? 'ok' : 'fail')}
                      </span>
                    </td>
                    <td className="mono small">{row.connector_id}</td>
                    <td>
                      <Link to={`/executions/${row.id}`} className="mono">
                        {row.action_id}
                      </Link>
                      {row.error_message && (
                        <div
                          className="small truncate"
                          style={{ color: 'var(--danger)', maxWidth: 340 }}
                        >
                          {row.error_message}
                        </div>
                      )}
                      {row.truncated && (
                        <span className="badge badge-neutral">truncated</span>
                      )}
                    </td>
                    <td className="small">{formatDuration(row.duration_ms)}</td>
                    <td className="small">{row.pages}</td>
                    <td className="small faint" title={row.executed_at ?? undefined}>
                      {relativeTime(row.executed_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="btn btn-sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← Newer
            </button>
            <span className="small faint">
              {offset + 1}–{offset + history.data.executions.length} of{' '}
              {history.data.total}
            </span>
            <button
              className="btn btn-sm"
              disabled={!history.data.has_more}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Older →
            </button>
          </div>
        </>
      )}
    </div>
  )
}
